import { useEffect, useRef, useState } from "react";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { AU_STATES } from "../../lib/format";
import { useBrand } from "../../context/SettingsContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";

const Section = ({ title, children }) => (
  <div className="rounded-lg border border-zinc-200 bg-white p-6">
    <h3 className="mb-4 font-display text-lg font-bold uppercase">{title}</h3>
    <div className="grid gap-4 md:grid-cols-2">{children}</div>
  </div>
);

export const CompanySettingsForm = () => {
  const [s, setS] = useState(null);
  const fileRef = useRef(null);
  const { refresh } = useBrand();
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const F = ({ k, label, mono, ...rest }) => (
    <div><Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-zinc-500">{label}</Label>
      <Input data-testid={`settings-${k}-input`} value={s[k] ?? ""} onChange={(e) => set(k, e.target.value)} className={mono ? "font-mono" : ""} {...rest} /></div>
  );

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Logo must be under 2MB");
    const reader = new FileReader();
    reader.onload = () => set("logo", reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    try {
      await api.put("/settings", { ...s, gst_rate: Number(s.gst_rate) || 0, quote_validity_days: Number(s.quote_validity_days) || 30 });
      toast.success("Company settings saved");
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="company-settings-form">
      <div className="rounded-lg border border-zinc-200 bg-white p-6">
        <h3 className="mb-4 font-display text-lg font-bold uppercase">Company logo</h3>
        <div className="flex items-center gap-6">
          <div className="grid h-28 w-56 place-items-center rounded-md border-2 border-dashed border-zinc-300 bg-slate-50 p-2">
            {s.logo ? <img data-testid="logo-preview" src={s.logo} alt="logo" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-zinc-400">No logo uploaded</span>}
          </div>
          <div className="space-y-2">
            <input ref={fileRef} data-testid="logo-file-input" type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={onLogo} />
            <Button data-testid="upload-logo-button" variant="outline" onClick={() => fileRef.current.click()}><Upload className="mr-1 h-4 w-4" />Upload logo</Button>
            {s.logo && <Button data-testid="remove-logo-button" variant="ghost" onClick={() => set("logo", "")} className="ml-2 text-red-600"><X className="mr-1 h-4 w-4" />Remove</Button>}
            <p className="text-xs text-zinc-500">PNG or JPG, max 2MB. Shown in the app header and on exported quotes.</p>
          </div>
        </div>
      </div>
      <Section title="Business details">
        {F({k: "legal_name", label: "Legal entity name"})}
        {F({k: "trading_name", label: "Trading name"})}
        {F({k: "abn", label: "ABN", mono: true, placeholder: "XX XXX XXX XXX"})}
        {F({k: "acn", label: "ACN", mono: true, placeholder: "XXX XXX XXX"})}
        {F({k: "licence_number", label: "Plumbing / contractor licence no.", mono: true})}
        <div className="flex items-end gap-6">
          <div className="flex items-center gap-2 pb-2"><Switch data-testid="settings-gst-switch" checked={s.gst_registered} onCheckedChange={(v) => set("gst_registered", v)} /><span className="text-sm">Registered for GST</span></div>
          {F({k: "gst_rate", label: "GST %", type: "number", mono: true})}
        </div>
      </Section>
      <Section title="Contact & address">
        {F({k: "phone", label: "Phone"})}
        {F({k: "email", label: "Email", type: "email"})}
        {F({k: "website", label: "Website"})}
        {F({k: "street", label: "Street"})}
        {F({k: "suburb", label: "Suburb"})}
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-zinc-500">State</Label>
            <select data-testid="settings-state-select" value={s.state} onChange={(e) => set("state", e.target.value)} className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm">{AU_STATES.map((x) => <option key={x}>{x}</option>)}</select></div>
          {F({ k: "postcode", label: "Postcode", maxLength: 4, mono: true })}
        </div>
      </Section>
      <Section title="Bank details (shown on quotes)">
        {F({k: "bank_name", label: "Bank"})}
        {F({k: "bank_account_name", label: "Account name"})}
        {F({k: "bsb", label: "BSB", mono: true, placeholder: "XXX-XXX"})}
        {F({k: "account_number", label: "Account number", mono: true})}
      </Section>
      <div className="rounded-lg border border-zinc-200 bg-white p-6">
        <h3 className="mb-4 font-display text-lg font-bold uppercase">Quote defaults</h3>
        <div className="max-w-xs">{F({k: "quote_validity_days", label: "Quote validity (days)", type: "number", mono: true})}</div>
        <div className="mt-4"><Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-zinc-500">Terms & conditions</Label>
          <Textarea data-testid="settings-terms-input" rows={5} value={s.quote_terms} onChange={(e) => set("quote_terms", e.target.value)} /></div>
      </div>
      <div className="flex justify-end"><Button data-testid="save-settings-button" onClick={save} className="bg-red-600 hover:bg-red-700">Save company settings</Button></div>
    </div>
  );
};
